import { FC, useEffect, useRef } from 'react';

import { useCookieConsentDecision } from '@eneco/hooks/cookieConsent';
import { useRouter } from '@eneco/next';
import type { UsabillaFeedbackRendering } from '@sitecore/types/UsabillaFeedback';

const UsabillaFeedback: FC<UsabillaFeedbackRendering> = ({ fields }) => {
  const element = useRef<HTMLDivElement>(null);
  const hasCookieConsentDecision = useCookieConsentDecision();
  const router = useRouter();

  let experienceEditorText = '';
  if (fields.inPageSurveyIdText?.editable) {
    experienceEditorText = 'placeholder for usabilla component';
  }

  // Clear the widget on in-page navigation. The host element is missing until
  // cookie consent is given, so the handler reads the ref at event time.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRouteChangeStart = () => {
      const ubElement = element.current;
      if (!ubElement?.childNodes.length) return;

      while (ubElement.firstChild) {
        ubElement.firstChild.remove();
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router]);

  useEffect(() => {
    const ubElement = element.current;

    if (!ubElement || !hasCookieConsentDecision) return;

    /**
     * Usabilla traverses the parents of the `ub-in-page` element and checks
     * their `getBoundingClientRect` dimensions to set the dimensions of its
     * iframe. As elements with `display: contents` return dimensions with
     * value `0`, the iframe is displayed with the same dimensions.
     *
     * By overwriting the `getBoundingClientRect` method of these elements with
     * their parent's (which is exactly what they display as), this Usabilla
     * issue is fixed.
     */
    let parent = ubElement.parentElement;
    while (parent?.parentElement) {
      if (window.getComputedStyle(parent)?.display === 'contents') {
        parent.getBoundingClientRect = function (parent: HTMLElement) {
          // @ts-ignore: parent.parentElement is already checked to exist in while loop
          return parent.parentElement.getBoundingClientRect();
        }.bind(this, parent);
      }
      parent = parent.parentElement;
    }
  }, [hasCookieConsentDecision]);

  if (!hasCookieConsentDecision) {
    return null;
  }

  const usabillaAttributes = {
    'ub-in-page': fields.inPageSurveyIdText?.value,
    'ub-in-page-category': fields.inPageCategoryText?.value,
    'ub-in-page-subcategory': fields.inPageSubCategoryText?.value,
    'ub-in-page-item': fields.inPageSurveyItemText?.value,
  };

  return (
    <div {...usabillaAttributes} ref={element}>
      {experienceEditorText}
    </div>
  );
};

export { UsabillaFeedback };
export default UsabillaFeedback;
